import { MdOutlineChevronRight } from 'react-icons/md';
import { CovalentBalanceItem } from '@shared/types/frontend';
import AssetLogo from '@/components/shared/AssetLogo';
import classNames from '@/lib/misc/classNames';
import { toLocaleShortString } from '@/lib/misc/utils';
type ModeType = 'From' | 'To' | 'Buy' | 'Sell';
interface AssetSelectionMobileProps {
  mode: ModeType;
  setSelectFromAsset?: (value: boolean) => void;
  setSelectToAsset?: (value: boolean) => void;
  desktop: boolean;
  assets: CovalentBalanceItem[];
}
interface AssetItemProps {
  asset: CovalentBalanceItem;
  label: string;
  showRightBorder?: boolean;
  percentage?: number;
}
const AssetItem = ({
  asset,
  label,
  showRightBorder = true,
  percentage = null
}: AssetItemProps) => {
  return <div className={classNames('flex flex-col items-start ml-8', {
    'lg:border-r border-genius-blue pr-7': showRightBorder
  })} data-sentry-component="AssetItem" data-sentry-source-file="AssetSelectionMobile.tsx">
      <p className='text-xs text-genius-cream/60'>{label}</p>
      <div className='flex items-center mt-4'>
        <div className='flex items-center flex-shrink-0'>
          <AssetLogo asset={asset} withTickerSymbol data-sentry-element="AssetLogo" data-sentry-source-file="AssetSelectionMobile.tsx" />
          {percentage && <p className='text-xs text-genius-cream/60'>&nbsp;({percentage?.toFixed(2)}%)</p>}
        </div>
        <MdOutlineChevronRight size={16} data-sentry-element="MdOutlineChevronRight" data-sentry-source-file="AssetSelectionMobile.tsx" />
      </div>
    </div>;
};
interface MobileAssetsProps {
  assets: CovalentBalanceItem[];
  mode: ModeType;
}
const MobileAssets = ({
  assets,
  mode
}: MobileAssetsProps) => {
  const isSingleAsset = assets.length === 1;
  const multipleAssetsLogos = () => {
    return <div className='flex' data-sentry-component="multipleAssetsLogos" data-sentry-source-file="AssetSelectionMobile.tsx">
        {assets?.slice(0, 2)?.map((asset, index) => <AssetLogo key={asset.contract_display_name} asset={asset} className={index === 0 ? '' : '-translate-x-4'} />)}
      </div>;
  };
  const assetsNames = () => {
    if (isSingleAsset) {
      return assets[0]?.contract_ticker_symbol ?? assets[0]?.contract_name;
    }
    const firstTwoAssets = assets.slice(0, 2);
    const firstTwoAssetsNames = firstTwoAssets.map(asset => toLocaleShortString(asset.contract_ticker_symbol ?? asset.contract_name, 5)).join(' and ');
    if (assets.length > 2) {
      return `${firstTwoAssetsNames} +${assets.length - 2}`;
    }
    return firstTwoAssetsNames;
  };
  return <div className='flex flex-col items-start px-4 w-full' data-sentry-component="MobileAssets" data-sentry-source-file="AssetSelectionMobile.tsx">
      <div className='flex items-center py-2 relative w-full'>
        <div className='flex items-center w-full'>
          {isSingleAsset ? <AssetLogo className={`${mode === 'To' && 'ml-2'}`} showNetwork={true} asset={assets[0]} /> : multipleAssetsLogos()}
          <div>
            <p className='text-xs  font-light text-left ml-4 text-genius-cream/50'>{mode}</p>
            <p className='text-xs font-light text-left ml-4 text-genius-cream'>{assetsNames()}</p>
          </div>
        </div>
        <MdOutlineChevronRight className='absolute right-5' size={16} data-sentry-element="MdOutlineChevronRight" data-sentry-source-file="AssetSelectionMobile.tsx" />
      </div>
    </div>;
};
const EmptyState = ({
  mode
}: {
  mode: ModeType;
}) => {
  return <div className='text-xs text-left mx-8 flex items-center' data-sentry-component="EmptyState" data-sentry-source-file="AssetSelectionMobile.tsx">
      <div>
        <p className='text-genius-cream/60'>{mode}</p>
        <p>Select</p>
      </div>
      <MdOutlineChevronRight className='absolute right-5' size={16} data-sentry-element="MdOutlineChevronRight" data-sentry-source-file="AssetSelectionMobile.tsx" />
    </div>;
};
const AssetSelectionMobile = ({
  mode,
  setSelectFromAsset,
  setSelectToAsset,
  desktop,
  assets
}: AssetSelectionMobileProps) => {
  const handleSelectAsset = () => {
    if (mode === 'From') {
      setSelectFromAsset(true);
    } else if (mode === 'To') {
      setSelectToAsset(true);
    }
  };
  return <div className={classNames('flex items-center h-[71px] rounded-xl gap-2 w-full hover:cursor-pointer transition-all duration-300  text-genius-cream border-genius-blue border-t-[1px] border-b-[1px]  relative', {
    'w-1/2': mode === 'From' || mode === 'To',
    'mr-1 ml-1': mode === 'From',
    'mr-1': mode === 'To',
    'bg-genius-blue': assets?.length,
    'gradient-genius-sides': !assets?.length
  })} onClick={handleSelectAsset} data-sentry-component="AssetSelectionMobile" data-sentry-source-file="AssetSelectionMobile.tsx">
      <div className='flex items-center w-full'>
        {!assets?.length && <EmptyState mode={mode} />}
        {!!assets?.length && <>
            {mode === 'From' && <>
                {desktop && <div className='flex flex-col lg:flex-row'>
                    {assets?.map((asset, index) => <AssetItem key={asset.contract_ticker_symbol} asset={asset} label={index ? 'And' : mode} showRightBorder={index !== assets.length - 1} />)}
                  </div>}
                {!desktop && <MobileAssets assets={assets} mode={mode} />}
              </>}
            {mode === 'To' && <MobileAssets assets={assets} mode={mode} />}
          </>}
      </div>
    </div>;
};
export default AssetSelectionMobile;